import { useState, useCallback } from 'react';
import { Box, Typography, IconButton, Drawer, Button } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import useApi from 'hooks/useApi'; // <-- add this

const MAX_FILES = 1;

type FileUploadDrawerProps = {
  maxSize?: number;
  value: any[]; // Now array of uploadAsset responses
  onChange: (files: any[]) => void;
  entityId?: string;
  entityType?: string;
};

const FileUploadDrawer = ({ maxSize = MAX_FILES, value = [], onChange, entityId, entityType }: FileUploadDrawerProps) => {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { uploadAsset } = useApi(); // <-- get uploadAsset

  const finalMaxSize = maxSize === 0 ? MAX_FILES : maxSize;

  const toggleDrawer = (state: boolean) => () => {
    setOpen(state);
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (value.length + acceptedFiles.length > finalMaxSize) {
        alert(`You can only upload up to ${finalMaxSize} files.`);
        return;
      }
      setUploading(true);
      try {
        const keyUid = entityId;
        console.log(keyUid, 'keyUid');
        const path = keyUid ? `transaction/${keyUid}/files` : 'uploads';
        const uploaded = await Promise.all(
          acceptedFiles.map(async (file) => {
            const res = await uploadAsset(file, path);
            return res.data;
          })
        );
        if (onChange) onChange([...value, ...uploaded]);
      } finally {
        setUploading(false);
      }
    },
    [value, onChange, finalMaxSize, uploadAsset, entityId]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true
    // Removed accept restriction to allow all file types
  });

  const removeFile = (identifier: string) => {
    if (onChange) {
      onChange(
        value.filter((file: any) => {
          const filePath = file.file_path || file.path || file.name;
          return filePath !== identifier;
        })
      );
    }
  };

  const renderPreview = (file: any) => {
    // Handle different possible file object structures
    const fileName = file.file_name || file.name || 'Unknown file';
    const url = file.url;
    const mimeType = file.mime_type || file.type;
    const filePath = file.file_path || file.path || fileName;

    const isImage = mimeType?.startsWith('image/');

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper'
        }}
      >
        {isImage && url ? (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              flexShrink: 0
            }}
          >
            <img
              src={url}
              alt={fileName}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'grey.50',
              flexShrink: 0
            }}
          >
            <InsertDriveFileIcon
              sx={{
                fontSize: 20,
                color: 'primary.main'
              }}
            />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={fileName}
          >
            {fileName}
          </Typography>
          {mimeType && (
            <Typography variant="caption" color="text.secondary">
              {mimeType.split('/')[1]?.toUpperCase() || 'FILE'}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={() => removeFile(filePath)} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  return (
    <>
      <Button onClick={toggleDrawer(true)} variant="outlined" sx={{fontSize: '12px', textTransform: 'none'}}>
        Upload/View Documents
      </Button>

      <Drawer
        anchor="right"
        open={open}
        onClose={toggleDrawer(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 400,
            p: 3
          },
           zIndex: 1300
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Documents</Typography>
          <IconButton onClick={toggleDrawer(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          {...getRootProps()}
          sx={{
            border: isDragActive ? '3px dashed #1976d2' : '2px dashed #ccc',
            borderRadius: 2,
            bgcolor: isDragActive ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
            p: 4,
            mb: 3,
            textAlign: 'center',
            cursor: uploading ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: '#1976d2',
              bgcolor: 'rgba(25, 118, 210, 0.04)'
            },
            minHeight: 120,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <input {...getInputProps()} />
          {isDragActive ? (
            <Typography color="primary" variant="h6" sx={{ mb: 1 }}>
              Drop the files here…
            </Typography>
          ) : (
            <>
              <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                Drag & drop files here
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                or click to browse from your device
              </Typography>
            </>
          )}
          {!isDragActive && (
            <Button variant="contained" component="span" disabled={uploading} sx={{ mt: 1 }} size="small">
              {uploading ? 'Uploading...' : 'Choose Files'}
            </Button>
          )}
        </Box>

        <Typography variant="subtitle1" mb={1}>
          Uploaded Documents
        </Typography>
        {value.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No documents uploaded yet.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {value.map((file, index) => (
              <Box key={index}>{renderPreview(file)}</Box>
            ))}
          </Box>
        )}
      </Drawer>
    </>
  );
};

export default FileUploadDrawer;
